import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemParams, DgColumnDataType, DgColumnWidths, eSimApiPaths } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DgActionColumn } from 'src/app/core/interfaces/datagrid/dg-action-column';
import { DgApiPath } from 'src/app/core/interfaces/datagrid/dg-api-paths';
import { DgColumn } from 'src/app/core/interfaces/datagrid/dg-column';
import { DgPopup } from 'src/app/core/interfaces/datagrid/dg-pop-up';
import { DgProperties } from 'src/app/core/interfaces/datagrid/dg-properties';
import { EncryptionService } from 'src/app/core/services/utelize/general/encryption-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { firstValueFrom } from 'rxjs';
import { GridViewDefaults } from 'src/app/core/grid-view/component/grid-view.defaults';
import { GridViewComponent } from 'src/app/core/grid-view/component/grid-view.component';
import { GridViewService } from 'src/app/core/services/utelize/general/grid-view.service';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { UserDTO } from 'src/app/core/models/system/user/user.dto';
import { Validators } from '@angular/forms';

const apiPath             =environment.eSimApi+eSimApiPaths.Users + "/" + eSimApiPaths.GetUsersPortal;
const exportFilenameSuffix="Portal-User-Data";
const popUpTitle          ="Portal User";

@Component({
  selector: 'app-users-portal-embedded',
  templateUrl: './users-portal.component.html',
  styleUrls: ['./users-portal.component.scss'],
  providers: [GridViewService]
})

export class UsersPortalEmbeddedComponent implements AfterViewInit, OnChanges, OnInit {
  
  @Input() companyId : number | undefined;
  
  @Output() gridAddClick : EventEmitter<void>   =new EventEmitter<void>();
  @Output() gridEditClick: EventEmitter<UserDTO>=new EventEmitter<UserDTO>();
  
  @ViewChild('gridView', { static: false }) gridView!: GridViewComponent;

  get embeddedGridHeight() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.ModalGridHeightEmbedded, 420); }
  
  private gridViewDefaults!: GridViewDefaults;
  
  public curUser        : SystemUserDTO;
  public dgApiPath!     : DgApiPath | undefined;
  public dgActionColumn!: DgActionColumn | undefined;
  public dgColumns!     : Array<DgColumn>;
  public dgPopup!       : DgPopup | undefined;
  public dgProperties!  : DgProperties | undefined;
  public gotDgProperties: boolean=false;
  
  constructor(
    private authenticationService     : AuthenticationService,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private encryptionService         : EncryptionService,
    private exceptionsService         : ExceptionsService
  ) {
    this.curUser         =this.authenticationService.currentUserObject();
    this.gridViewDefaults=new GridViewDefaults(this.authenticationService, this.componentAccessUserService, this.configSystemService, this.exceptionsService);
  }

  public async ngOnInit(): Promise<void> {
    try {
      if (this.companyId==undefined) {
        throw new Error("PortalUsersEmbeddedComponent - CompanyID cannot be determined");
      }

      await this.setUpDgApiPaths();
      this.setUpDgActionColumn();
      this.setUpDgColumns();
      this.setUpDgPopup();
      this.setUpDgProperties();

      this.gotDgProperties=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private async setUpDgApiPaths(): Promise<void> {
    try {
      this.dgApiPath            =this.gridViewDefaults.setUpDgApiPaths(apiPath);
      this.dgApiPath!.delete    =environment.eSimApi+eSimApiPaths.Users;
      this.dgApiPath!.refreshGet=() => this.setUpDgApiPathGet();

      await this.setUpDgApiPathGet();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async setUpDgApiPathGet(): Promise<void> {
    try {
      const encResult     =await firstValueFrom(this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>this.companyId, true));
      const companyIdEncSS=encResult.result;

      this.dgApiPath!.get=apiPath + "?companyIdEncSS=" + companyIdEncSS;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgActionColumn() {
    try {
      this.dgActionColumn                                  =this.gridViewDefaults.setUpDgActionColumn();
      this.dgActionColumn!.deleteButtonDisabledHint        ="You cannot delete your own user account";
      this.dgActionColumn!.deleteRowButtonDisabledCondition=(data:any) => {return data?.id===this.curUser.userId;};
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgPopup() {
    try {
      this.dgPopup               =this.gridViewDefaults.setUpDgPopup(popUpTitle);
      this.dgPopup!.overridePopup=true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgProperties() {
    try {
      this.dgProperties                 =this.gridViewDefaults.setUpDgProperties("", exportFilenameSuffix + "-" + this.companyId);
      this.dgProperties!.encryptDeleteSS=true;
      this.dgProperties!.gridKey        ="EmbeddedUsersPortal";
      this.dgProperties!.height         =this.embeddedGridHeight;
      this.dgProperties!.pageSize       =10;
      this.dgProperties!.showAddButton  =true;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setUpDgColumns() {
    try {
          let dgColumn: DgColumn | undefined;
          
          //ID
          dgColumn             =this.gridViewDefaults.setUpDgColumn("id", "ID", 0, DgColumnDataType.number);
          dgColumn!.formVisible=false;
          dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridShowId, false);
          dgColumn!.width      =DgColumnWidths.id;
          this.dgColumns       =[dgColumn!];
          
          //InviewUserID
          // dgColumn            =this.gridViewDefaults.setUpDgColumn("inviewUserID", "Inview ID", "", DgColumnDataType.number);
          // dgColumn!.validators=[Validators.required];
          // dgColumn!.width     =DgColumnWidths.id;
          // this.dgColumns.push(dgColumn!);
          
          //CompanyID
          // dgColumn                    =this.gridViewDefaults.setUpDgColumn("companyID", "Company", "", DgColumnDataType.number);
          // dgColumn!.dataSource        =this.companiesService.idAndDescriptionLookupDataSourceForLoggedInUser(this.curUserID);
          // dgColumn!.dataSourceForAForm=this.companiesService.getIdAndDescriptionForLoggedInUser(this.curUserID);
          // dgColumn!.gridVisible       =false;
          // dgColumn!.validators        =[Validators.required];
          // this.dgColumns.push(dgColumn!);
    
          //CompanyName
          // dgColumn            =this.gridViewDefaults.setUpDgColumn("companyName", "Company", "", DgColumnDataType.string);
          // dgColumn!.validators=[Validators.required, Validators.maxLength(100)];
          // dgColumn!.width     =DgColumnWidths.companyName;
          // this.dgColumns.push(dgColumn!);
    
          //Fullname
          dgColumn             =this.gridViewDefaults.setUpDgColumn("fullname", "Username", "", DgColumnDataType.string);
          dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridUsersPortalShowFullname, true);
          this.dgColumns.push(dgColumn!);
    
          //Firstname
          // dgColumn            =this.gridViewDefaults.setUpDgColumn("firstname", "Firstname", "", DgColumnDataType.string);
          // dgColumn!.validators=[Validators.required, Validators.maxLength(100)];
          // this.dgColumns.push(dgColumn!);
    
          //Lastname
          // dgColumn            =this.gridViewDefaults.setUpDgColumn("lastname", "Lastname", "", DgColumnDataType.string);
          // dgColumn!.validators=[Validators.required, Validators.maxLength(100)];
          // this.dgColumns.push(dgColumn!);
    
          //Username
          // dgColumn             =this.gridViewDefaults.setUpDgColumn("username", "Username", "", DgColumnDataType.string);
          // dgColumn!.gridVisible=this.configSystemService.getValueAsBoolean(ConfigSystemParams.GridUsersPortalShowUsername, false);
          // dgColumn!.validators =[Validators.required, Validators.maxLength(100)];
          // this.dgColumns.push(dgColumn!);
    
          //Email
          dgColumn            =this.gridViewDefaults.setUpDgColumn("email", "Email", "", DgColumnDataType.string);
          dgColumn!.validators=[Validators.required, Validators.maxLength(100)];
          dgColumn!.width     =DgColumnWidths.email;
          this.dgColumns.push(dgColumn!);
    
          //Mobile
          dgColumn            =this.gridViewDefaults.setUpDgColumnMobile("mobile", "Mobile");
          dgColumn!.validators=[Validators.required, Validators.maxLength(25)];
          this.dgColumns.push(dgColumn!);
    
          //AccessTypeID
          // dgColumn                    =this.gridViewDefaults.setUpDgColumn("accessTypeID", "Role", "", DgColumnDataType.number);
          // dgColumn!.dataSource        =this.typesAccessService.idAndDescriptionLookupDataSource();
          // dgColumn!.dataSourceForAForm=this.typesAccessService.getIdAndDescription();
          // dgColumn!.validators        =[Validators.required];
          // this.dgColumns.push(dgColumn!);
    
          //AccessType
          dgColumn=this.gridViewDefaults.setUpDgColumn("accessType", "Role", "", DgColumnDataType.string);
          this.dgColumns.push(dgColumn!);
          
          //InviewCostCentreID
          // dgColumn             =this.gridViewDefaults.setUpDgColumn("inviewCostCentreID", "Inview CC ID", "", DgColumnDataType.number);
          // dgColumn!.gridVisible=false;
          // //dgColumn!.validators =[Validators.required];
          // this.dgColumns.push(dgColumn!);
    
          //PortalUser
          // dgColumn                    =this.gridViewDefaults.setUpDgColumn("portalUser", "Portal User", true, DgColumnDataType.boolean);
          // dgColumn!.dataSource        =this.booleanService.idAndDescriptionLookupDataSource();
          // dgColumn!.dataSourceForAForm=this.booleanService.getIdAndDescription();
          // dgColumn!.gridVisible       =false;
          // //dgColumn!.validators        =[Validators.required];
          // this.dgColumns.push(dgColumn!);
    
          //Tag
          dgColumn             =this.gridViewDefaults.setUpDgColumn("tag", "Tag", "", DgColumnDataType.string);
          dgColumn!.gridVisible=false;
          dgColumn!.validators =[Validators.maxLength(50)];
          this.dgColumns.push(dgColumn!);
          
          //LastPortalLoginDate
          // dgColumn       =this.gridViewDefaults.setUpDgColumn("lastPortalLoginDate", "Last Portal Login", "", DgColumnDataType.datetime);
          // dgColumn!.width=DgColumnWidths.dateWithTime;
          // this.dgColumns.push(dgColumn!);
    
          //Guid
          // dgColumn             =this.gridViewDefaults.setUpDgColumn("guid", "Guid", "", DgColumnDataType.string);
          // dgColumn!.gridVisible=false;
          // this.dgColumns.push(dgColumn!);
          
          //Active
          // dgColumn                    =this.gridViewDefaults.setUpDgColumn("active", "Active", true, DgColumnDataType.boolean);
          // dgColumn!.dataSource        =this.booleanService.idAndDescriptionLookupDataSource();
          // dgColumn!.dataSourceForAForm=this.booleanService.getIdAndDescription();
          // dgColumn!.gridVisible       =false;
          // dgColumn!.validators        =[Validators.required];
          // this.dgColumns.push(dgColumn!);
    
          //EnteredByName
          // dgColumn             =this.gridViewDefaults.setUpDgColumn("enteredByName", "Entered By", this.curUserID, DgColumnDataType.string);
          // dgColumn!.formVisible=false;
          // dgColumn!.gridVisible=false;
          // this.dgColumns.push(dgColumn!);
    
          //EnteredDate
          // dgColumn             =this.gridViewDefaults.setUpDgColumn("enteredDate", "Entered Date", "", DgColumnDataType.datetime);
          // dgColumn!.formVisible=false;
          // dgColumn!.gridVisible=false;
          // dgColumn!.sendToDB   =false;
          // this.dgColumns.push(dgColumn!);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public gridAddButtonClick(e: any) {
    try {
      this.gridAddClick.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public gridEditButtonClick(e: any) {
    try {
      const maybeUser=e.row?.data;

      if (this.isUserDTO(maybeUser)) {
        this.gridEditClick.emit(maybeUser as UserDTO);

      } else {
        throw new Error("PortalUsersEmbeddedComponent - Invalid UserDTO");
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private isUserDTO(obj: any): obj is UserDTO {
    try {
      return obj                               &&
             typeof obj.companyID ==="number"  &&
             typeof obj.email     ==="string"  &&
             typeof obj.eSimUser  ==="boolean" &&
             typeof obj.portalUser==="boolean" &&
             typeof obj.username  ==="string";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }
}
